"use client";

import React, { useState } from "react";
import {
  IconArrowUpRight,
  IconBrandLinkedin,
  IconBrandWhatsapp,
  IconMail,
  IconSend,
} from "@tabler/icons-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { contact } from "@/constants/site";
import SectionHeading from "@/components/home/SectionHeading";

const channels = [
  { label: "WhatsApp", value: "+92 305 7474003", href: contact.whatsapp, icon: IconBrandWhatsapp },
  { label: "Email", value: contact.email, href: `mailto:${contact.email}`, icon: IconMail },
  { label: "LinkedIn", value: "subhan-nadeem-dev", href: contact.linkedin, icon: IconBrandLinkedin },
];

const fieldClass =
  "h-[46px] rounded-[12px] border-white/[0.08] bg-[#0E1018] text-white placeholder:text-[#5C6670] focus-visible:border-[#48AEEF]/60 focus-visible:ring-[#48AEEF]/20";

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function ContactSection() {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<null | { ok: boolean; msg: string }>(null);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    const form = e.currentTarget;
    const fd = new FormData(form);

    const name = ((fd.get("name") as string) || "").trim();
    const email = ((fd.get("email") as string) || "").trim();
    const phone = ((fd.get("phone") as string) || "").trim(); // optional
    const subject = ((fd.get("subject") as string) || "").trim();
    const message = ((fd.get("message") as string) || "").trim();
    const website = ((fd.get("website") as string) || "").trim(); // honeypot

    if (!name || !email || !subject || !message) {
      setStatus({ ok: false, msg: "Please fill in your name, email, subject, and message." });
      setLoading(false);
      return;
    }

    if (!isValidEmail(email)) {
      setStatus({ ok: false, msg: "Please enter a valid email address." });
      setLoading(false);
      return;
    }

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, phone, subject, message, website }),
      });
      const json = await res.json();

      if (!res.ok || !json.ok) {
        setStatus({ ok: false, msg: "Failed to send. Please try again." });
      } else {
        setStatus({ ok: true, msg: "Message sent! I’ll get back to you soon." });
        form.reset();
      }
    } catch {
      setStatus({ ok: false, msg: "Network error. Please try again." });
    } finally {
      setLoading(false);
    }
  }

  return (
    <section id="contact" className="scroll-mt-32 rounded-[20px] border border-white/[0.06] bg-black p-5 sm:p-6 md:p-10">
      <SectionHeading
        eyebrow="Contact"
        title="Let's talk about your project"
        subtitle="Tell me what your team does manually today. I'll reply with how I'd automate it and a rough plan."
      />

      <div className="mt-8 grid grid-cols-[minmax(0,1fr)] gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <div className="flex flex-col gap-3">
          {channels.map(({ label, value, href, icon: Icon }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="group flex items-center gap-4 rounded-[16px] border border-white/[0.06] bg-[#0E1018] p-4 transition-colors hover:border-white/15 hover:bg-[#111521]"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[12px] bg-[#48AEEF]/10 text-[#48AEEF]">
                <Icon size={24} />
              </div>
              <div className="min-w-0 flex-1">
                <span className="block text-[12px] font-semibold uppercase tracking-[0.12em] text-[#5C6670]">
                  {label}
                </span>
                <span className="block text-[15px] font-semibold [overflow-wrap:anywhere]">{value}</span>
              </div>
              <IconArrowUpRight
                size={18}
                className="shrink-0 text-[#5C6670] transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-white"
              />
            </a>
          ))}

          <div className="mt-auto rounded-[16px] border border-emerald-400/20 bg-emerald-400/[0.04] p-4 text-[14px] leading-relaxed text-[#9AA3AD]">
            <span className="mb-1 flex items-center gap-2 font-semibold text-emerald-300">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              Currently taking new projects
            </span>
            Automation, configurators, management systems and AI agents, for any industry.
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          noValidate
          className="@container rounded-[16px] border border-white/[0.06] bg-[#0B0D14] p-5 md:p-6"
        >
          {/* Honeypot (hidden) */}
          <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" />

          <div className="grid gap-4 @lg:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-[13px] font-medium text-[#A5B0BC]" htmlFor="name">
                Your name
              </label>
              <Input id="name" name="name" placeholder="Michael Carter" className={fieldClass} required />
            </div>
            <div>
              <label className="mb-1.5 block text-[13px] font-medium text-[#A5B0BC]" htmlFor="email">
                Your email
              </label>
              <Input id="email" name="email" type="email" placeholder="you@company.com" className={fieldClass} required />
            </div>
            <div>
              <label className="mb-1.5 block text-[13px] font-medium text-[#A5B0BC]" htmlFor="phone">
                Phone <span className="text-[#5C6670]">(optional)</span>
              </label>
              <Input id="phone" name="phone" type="tel" placeholder="+1 555 000 0000" className={fieldClass} />
            </div>
            <div>
              <label className="mb-1.5 block text-[13px] font-medium text-[#A5B0BC]" htmlFor="subject">
                Subject
              </label>
              <Input id="subject" name="subject" placeholder="e.g. Automate quoting" className={fieldClass} required />
            </div>
          </div>

          <div className="mt-4">
            <label className="mb-1.5 block text-[13px] font-medium text-[#A5B0BC]" htmlFor="message">
              Your message
            </label>
            <Textarea
              id="message"
              name="message"
              placeholder="What does your team do today, which tools do you use, and what should happen automatically?"
              className={`${fieldClass} h-auto min-h-[140px] py-3`}
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="mt-5 inline-flex h-12 w-full items-center justify-center gap-2 rounded-[12px] bg-white text-[16px] font-bold text-black transition-colors hover:bg-white/90 disabled:opacity-60"
          >
            <IconSend size={18} />
            {loading ? "Sending..." : "Send message"}
          </button>

          <p
            aria-live="polite"
            className={`mt-3 min-h-[1.25rem] text-sm ${
              status?.ok ? "text-green-400" : status ? "text-red-400" : "text-transparent"
            }`}
          >
            {status?.msg ?? "placeholder"}
          </p>
        </form>
      </div>
    </section>
  );
}
